import characters from "../Characters.json";
import { FOIL_CHANCE, drawWeightedCards } from "./cardOdds";
import {
    CORGI_PACK_DUPLICATE_REFUND,
    CORGI_PACK_SIZE,
    SPECIAL_CORGI_PACK_DUPLICATE_REFUND,
    clampCorgiPackGrantCount,
} from "./treats";

const normalizeId = (id) => {
    const n = Number(id);
    return Number.isFinite(n) ? n : id;
};

const isSpecialCorgiDrop = (drop = {}) =>
    drop?.rarity === "alpha" ||
    drop?.rarity === "gem" ||
    drop?.rarity === "founder";

function getDuplicateRefund(drop = {}) {
    return isSpecialCorgiDrop(drop)
        ? SPECIAL_CORGI_PACK_DUPLICATE_REFUND
        : CORGI_PACK_DUPLICATE_REFUND;
}

function sumDuplicateRefunds(drops = []) {
    return (drops || [])
        .filter((drop) => drop.isDuplicate)
        .reduce((sum, drop) => sum + getDuplicateRefund(drop), 0);
}

/**
 * Open a Corgi Pack: 5 unique cards using Endless board weights, then a
 * 1% foil stamp per card (same as Endless boards). Duplicates vs current
 * collection are flagged for a small Treats refund.
 */
export function openCorgiPack({
    catalog = characters,
    size = CORGI_PACK_SIZE,
    ownedIds = [],
    ownedFoilIds = [],
    random = Math.random,
} = {}) {
    const cards = drawWeightedCards(catalog, size, {
        allowSpecialVariants: true,
        random,
    });
    const owned = new Set((ownedIds || []).map(normalizeId));
    const ownedFoil = new Set((ownedFoilIds || []).map(normalizeId));

    const drops = cards.map((card) => {
        const id = normalizeId(card.id);
        const isFoil = random() < FOIL_CHANCE;
        const isNew = !owned.has(id);
        const isNewFoil = isFoil && !ownedFoil.has(id);

        if (isNew) {
            owned.add(id);
        }
        if (isNewFoil) {
            ownedFoil.add(id);
        }

        return {
            id,
            name: card.name,
            image: card.image,
            rarity: card.rarity || null,
            isFoil,
            isNew,
            isNewFoil,
            isDuplicate: !isNew,
        };
    });

    const duplicateCount = drops.filter((drop) => drop.isDuplicate).length;
    const newCardCount = drops.filter((drop) => drop.isNew).length;
    const newFoilCount = drops.filter((drop) => drop.isNewFoil).length;

    return {
        packCount: 1,
        drops,
        newCardCount,
        duplicateCount,
        newFoilCount,
        refund: sumDuplicateRefunds(drops),
    };
}

/**
 * Open several packs in sequence so later packs see cards from earlier ones
 * as owned (duplicate refunds stay honest).
 */
export function openCorgiPacks({
    count = 1,
    catalog = characters,
    size = CORGI_PACK_SIZE,
    ownedIds = [],
    ownedFoilIds = [],
    random = Math.random,
} = {}) {
    const packCount = clampCorgiPackGrantCount(count);
    let nextOwnedIds = [...(ownedIds || [])];
    let nextOwnedFoilIds = [...(ownedFoilIds || [])];
    const packs = [];
    const drops = [];

    for (let index = 0; index < packCount; index += 1) {
        const pack = openCorgiPack({
            catalog,
            size,
            ownedIds: nextOwnedIds,
            ownedFoilIds: nextOwnedFoilIds,
            random,
        });
        packs.push(pack);
        drops.push(...pack.drops);
        const applied = applyPackDropsToCollection(
            pack.drops,
            nextOwnedIds,
            nextOwnedFoilIds
        );
        nextOwnedIds = applied.collectionUnlocked;
        nextOwnedFoilIds = applied.collectionFoilUnlocked;
    }

    const duplicateCount = drops.filter((drop) => drop.isDuplicate).length;
    const newCardCount = drops.filter((drop) => drop.isNew).length;
    const newFoilCount = drops.filter((drop) => drop.isNewFoil).length;

    return {
        packCount,
        packs,
        drops,
        newCardCount,
        duplicateCount,
        newFoilCount,
        refund: sumDuplicateRefunds(drops),
    };
}

export function applyPackDropsToCollection(
    drops = [],
    collectionUnlocked = [],
    collectionFoilUnlocked = []
) {
    const regular = new Set((collectionUnlocked || []).map(normalizeId));
    const foil = new Set((collectionFoilUnlocked || []).map(normalizeId));
    let addedCards = false;
    let addedFoils = false;

    (drops || []).forEach((drop) => {
        const id = normalizeId(drop.id);
        if (drop.isNew && !regular.has(id)) {
            regular.add(id);
            addedCards = true;
        }
        if (drop.isNewFoil && !foil.has(id)) {
            foil.add(id);
            addedFoils = true;
        }
    });

    return {
        collectionUnlocked: Array.from(regular),
        collectionFoilUnlocked: Array.from(foil),
        addedCards,
        addedFoils,
    };
}
